import React, { useCallback, useEffect, useRef } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { BsPersonFill, BsPeopleFill } from 'react-icons/bs';
import { HiMiniUserGroup } from 'react-icons/hi2';

import {
  usePeersStore,
  useNotificationStore,
  useChatStore,
  useRoomStore,
  useVideoStreamStore,
  useAudioStreamStore,
} from '../stores';
import { toast } from 'react-toastify';
import SocketService from '../services/SocketService';

import { IPeer } from '../types';
import { ECommonSocketKeys } from '../types';

export interface IParticipantEvent {
  type: 'join' | 'leave';
  peer: {
    peer_id: string;
    peer_name: string;
    peer_uuid?: string;
  };
  timestamp: number;
}

// Utility functions that can be easily tested
export const formatJoinMessage = (events: IParticipantEvent[]): string => {
  if (events.length === 1) {
    return `${events[0].peer.peer_name} joined.`;
  } else if (events.length < 5) {
    const firstPeerName = events[0].peer.peer_name;
    const othersCount = events.length - 1;
    return `${firstPeerName} and ${othersCount} others joined.`;
  } else {
    return 'Several participants joined.';
  }
};

export const formatLeaveMessage = (events: IParticipantEvent[]): string => {
  if (events.length === 1) {
    return `${events[0].peer.peer_name} left.`;
  } else if (events.length < 5) {
    const firstPeerName = events[0].peer.peer_name;
    const othersCount = events.length - 1;
    return `${firstPeerName} and ${othersCount} others left.`;
  } else {
    return 'Several participants left.';
  }
};

export const formatNotificationMessage = (events: IParticipantEvent[]): string => {
  if (events.length === 0) return '';

  // Handle events based on their type
  if (events[0].type === 'join') {
    return formatJoinMessage(events);
  } else {
    return formatLeaveMessage(events);
  }
};

const useParticipantNotifications = ({ isConnected = false }: { isConnected: boolean }) => {
  const joinTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const joinEventsRef = useRef<IParticipantEvent[]>([]);
  const leaveEventsRef = useRef<IParticipantEvent[]>([]);
  const firstJoinEventTimestampRef = useRef<number | null>(null);
  const firstLeaveEventTimestampRef = useRef<number | null>(null);
  const pendingReconnectionsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const processedJoinIdsRef = useRef<Set<string>>(new Set());

  const currentPeerId = SocketService.getSocketId();

  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
    }))
  );

  // Resolve peer name from peer ID or provided name
  // to avoid an edge case where user recieves left event
  // without actually leaving (refreshing on MediaPreview) or instant reconnect
  const resolvePeerName = useCallback((peerId: string, providedName?: string) => {
    if (providedName && providedName.trim().length > 0) {
      return providedName;
    }

    const peerInfo = usePeersStore.getState().getPeerInfoById(peerId);
    return peerInfo?.peer_name;
  }, []);

  // Show notification with appropriate icon
  const showNotification = useCallback(
    (events: IParticipantEvent[]) => {
      if (events.length === 0) return;

      const message = formatNotificationMessage(events);
      if (message) {
        // Determine icon based on number of events and event type
        let iconElement: React.ReactNode = <BsPersonFill size={20} />;
        let iconColor = '#00ACFB'; // Default blue for join events (primary)

        // If we have leave events, use red color (danger)
        const eventType = events[0].type;
        if (eventType === 'leave') {
          iconColor = '#FF4D4F'; // Red for leave events (danger)
        }

        // Determine icon based on number of events
        if (events.length === 1) {
          iconElement = <BsPersonFill color={iconColor} size={20} />;
        } else if (events.length <= 5) {
          iconElement = <BsPeopleFill color={iconColor} size={20} />;
        } else {
          iconElement = <HiMiniUserGroup color={iconColor} size={20} />;
        }

        appendNotification({
          title: '',
          text: message,
          source: 'participants',
          icon: iconElement,
        });
      }
    },
    [appendNotification]
  );

  // Process join events
  const processJoinEvents = useCallback(() => {
    if (joinEventsRef.current.length > 0) {
      showNotification(joinEventsRef.current);
      joinEventsRef.current = [];
      firstJoinEventTimestampRef.current = null;
    }
    if (joinTimeoutRef.current) {
      globalThis.clearTimeout(joinTimeoutRef.current);
      joinTimeoutRef.current = null;
    }
  }, [showNotification]);

  // Process leave events
  const processLeaveEvents = useCallback(() => {
    if (leaveEventsRef.current.length > 0) {
      showNotification(leaveEventsRef.current);
      leaveEventsRef.current = [];
      firstLeaveEventTimestampRef.current = null;
    }
    if (leaveTimeoutRef.current) {
      globalThis.clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, [showNotification]);

  // Add leave event to pending list and set timeout
  const addLeaveEventToPending = useCallback(
    (event: IParticipantEvent) => {
      // Don't show notification for current user
      if (event.peer.peer_id === currentPeerId) return;

      // Track the timestamp of the first leave event
      if (firstLeaveEventTimestampRef.current === null) {
        firstLeaveEventTimestampRef.current = event.timestamp;
      }

      // Check if we've exceeded the 3-second window from the first event
      const timeSinceFirstEvent = event.timestamp - firstLeaveEventTimestampRef.current;

      // If more than 3 seconds have passed since the first event,
      // reset and start a new grouping window
      if (timeSinceFirstEvent >= 3000) {
        // Process existing events first
        if (leaveEventsRef.current.length > 0) {
          processLeaveEvents();
        }
        // Reset for new grouping window
        firstLeaveEventTimestampRef.current = event.timestamp;
        leaveEventsRef.current = [event]; // Start new array with current event
      } else {
        leaveEventsRef.current.push(event);
      }

      // Check if we should show "several participants" message immediately
      // This happens when we have more than 5 events
      const shouldShowSeveral = leaveEventsRef.current.length > 5;

      // Also check if multiple events happened simultaneously (same timestamp)
      const hasSimultaneousEvents =
        leaveEventsRef.current.length > 1 &&
        leaveEventsRef.current.every((e) => e.timestamp === leaveEventsRef.current[0].timestamp);

      // Reset the 500ms grouping timer on each event
      if (leaveTimeoutRef.current) {
        globalThis.clearTimeout(leaveTimeoutRef.current);
        leaveTimeoutRef.current = null;
      }

      // Set timeout based on conditions
      if (shouldShowSeveral || hasSimultaneousEvents) {
        processLeaveEvents();
      } else {
        // Set the normal 1 second grouping timeout from the latest event
        leaveTimeoutRef.current = setTimeout(() => {
          processLeaveEvents();
        }, 1000); // 1 second grouping window
      }
    },
    [currentPeerId, processLeaveEvents]
  );

  // Add join event to pending list and set timeout
  const addJoinEventToPending = useCallback(
    (event: IParticipantEvent) => {
      // Don't show notification for current user
      if (event.peer.peer_id === currentPeerId) return;

      if (firstJoinEventTimestampRef.current === null) {
        firstJoinEventTimestampRef.current = event.timestamp;
      }

      // Check if we've exceeded the 3-second window from the first event
      const timeSinceFirstEvent = event.timestamp - firstJoinEventTimestampRef.current;

      // If more than 3 seconds have passed since the first event,
      // reset and start a new grouping window
      if (timeSinceFirstEvent >= 3000) {
        // Process existing events first
        if (joinEventsRef.current.length > 0) {
          processJoinEvents();
        }
        // Reset for new grouping window
        firstJoinEventTimestampRef.current = event.timestamp;
        joinEventsRef.current = [event]; // Start new array with current event
      } else {
        joinEventsRef.current.push(event);
      }

      // Check if we should show "several participants" message immediately
      // This happens when we have more than 5 events
      const shouldShowSeveral = joinEventsRef.current.length > 5;

      // Also check if multiple events happened simultaneously (same timestamp)
      const hasSimultaneousEvents =
        joinEventsRef.current.length > 1 &&
        joinEventsRef.current.every((e) => e.timestamp === joinEventsRef.current[0].timestamp);

      // Reset the 500ms grouping timer on each event
      if (joinTimeoutRef.current) {
        globalThis.clearTimeout(joinTimeoutRef.current);
        joinTimeoutRef.current = null;
      }

      // Set timeout based on conditions
      if (shouldShowSeveral || hasSimultaneousEvents) {
        processJoinEvents();
      } else {
        if (joinTimeoutRef.current) {
          globalThis.clearTimeout(joinTimeoutRef.current);
        }
        joinTimeoutRef.current = setTimeout(() => {
          processJoinEvents();
        }, 1000); // 1 second grouping window
      }
    },
    [currentPeerId, processJoinEvents]
  );

  // Handle removeMe event
  const handleRemoveMe = useCallback(
    (data: { peer_id: string; peer_name: string; peer_counts?: number }, options?: { force?: boolean }) => {
      const resolvedName = resolvePeerName(data.peer_id, data.peer_name);

      if (!resolvedName || resolvedName.trim().length === 0) {
        return;
      }

      const trimmedName = resolvedName.trim();
      const currentPeerInfo = usePeersStore.getState().getPeerInfoById(data.peer_id);
      const peerUuid = currentPeerInfo?.peer_uuid;

      // CORE CLEANUP LOGIC (formerly forceRemovePeer)
      const performCleanup = () => {
        // 1. Remove Video And Audio
        useVideoStreamStore.getState().removeVideo(data.peer_id);
        useAudioStreamStore.getState().removeAudio(data.peer_id);
        usePeersStore.getState().updatePeerInfo(data.peer_id, { peer_video: false });

        // 2. Remove from stores
        usePeersStore.getState().deletePeer(data.peer_id);
        usePeersStore.getState().lowerHand(data.peer_id);
        useChatStore.getState().removePrivateChat(data.peer_id);

        // 3. Request Info cleanup
        const requestInfoList = useRoomStore.getState().requestInfo;
        const requestForLeavingPeer = requestInfoList.find((r) => r.peerId === data.peer_id);
        if (requestForLeavingPeer) {
          toast.dismiss(requestForLeavingPeer.requestId);
          useRoomStore.getState().removeRequestInfoByPeerId(data.peer_id);
        }
      };

      // IMMEDIATE REMOVAL (used by Reconnection Logic)
      // If forced, we assume it's a reconnection swap, so we DO NOT show a leave notification.
      if (options?.force) {
        performCleanup();
        return;
      }

      // STANDARD LEAVE: Gatekeeper Delay
      // Cancel existing timeout if any
      if (pendingReconnectionsRef.current.has(trimmedName)) {
        const existingTimeout = pendingReconnectionsRef.current.get(trimmedName);
        if (existingTimeout) globalThis.clearTimeout(existingTimeout);
      }

      const timeoutId = setTimeout(() => {
        pendingReconnectionsRef.current.delete(trimmedName);

        performCleanup();

        const event: IParticipantEvent = {
          type: 'leave',
          peer: {
            peer_id: data.peer_id,
            peer_name: resolvedName,
            peer_uuid: peerUuid,
          },
          timestamp: Date.now(),
        };

        addLeaveEventToPending(event);
      }, 2500);

      pendingReconnectionsRef.current.set(trimmedName, timeoutId);
    },
    [addLeaveEventToPending, resolvePeerName]
  );

  // Reconnection handling helper
  const handleReconnection = useCallback(
    (trimmedName: string, peerName: string, newPeerId: string, peerInfo: any) => {
      const timeoutId = pendingReconnectionsRef.current.get(trimmedName);
      if (timeoutId) {
        globalThis.clearTimeout(timeoutId);
      }
      pendingReconnectionsRef.current.delete(trimmedName);

      const peersMap = usePeersStore.getState().peers;
      for (const [existingId, peer] of Array.from(peersMap.entries())) {
        if (peer.peer_info?.peer_name === peerName && existingId !== newPeerId) {
          handleRemoveMe({ peer_id: existingId, peer_name: peerName }, { force: true });
        }
      }

      if ((window as any).roomClient) {
        (window as any).roomClient.setVideoOff(peerInfo, true);
      }
    },
    [handleRemoveMe]
  );

  // Handle new peer joined
  useEffect(() => {
    if (!isConnected) return;

    const handleNewPeerJoined = (data: IPeer) => {
      if (!data || !data.peer_info) return;

      const peerId = data.id ?? data.peer_info?.peer_id;
      const peerName = data.peer_info.peer_name;
      const peerUuid = data.peer_info.peer_uuid;

      // Ensure we have a valid name to display/track
      if (!peerName || peerName.trim().length === 0) {
        return;
      }

      const trimmedName = peerName.trim();

      // STRICT DEDUPLICATION: Ignore if we already processed this Peer ID recently (e.g. double event from server)
      if (processedJoinIdsRef.current.has(peerId)) {
        return;
      }
      processedJoinIdsRef.current.add(peerId);
      setTimeout(() => {
        processedJoinIdsRef.current.delete(peerId);
      }, 2000);

      // CHECK FOR RECONNECTION (Gatekeeper Logic)
      if (pendingReconnectionsRef.current.has(trimmedName)) {
        handleReconnection(trimmedName, peerName, peerId, data.peer_info);
        return;
      }

      // FRESH JOIN (or Reconnect > 2.5s)

      // 1. Add Video Box IMMEDIATELY (Sync with Notif)
      if ((window as any).roomClient) {
        (window as any).roomClient.setVideoOff(data.peer_info, true);
      }

      // 2. Show Join Notification
      const event: IParticipantEvent = {
        type: 'join',
        peer: {
          peer_id: peerId,
          peer_name: peerName.trim(),
          peer_uuid: peerUuid,
        },
        timestamp: Date.now(),
      };

      addJoinEventToPending(event);
    };

    SocketService.on(ECommonSocketKeys.NEW_PEER_JOINED, handleNewPeerJoined);

    return () => {
      if (isConnected) {
        SocketService.off(ECommonSocketKeys.NEW_PEER_JOINED, handleNewPeerJoined);
      }
    };
  }, [isConnected, addJoinEventToPending, currentPeerId, handleReconnection]);

  // Handle peer left (for regular meetings)
  useEffect(() => {
    if (!isConnected) return;

    SocketService.on(ECommonSocketKeys.REMOVE_ME, handleRemoveMe);

    return () => {
      if (isConnected) {
        SocketService.off(ECommonSocketKeys.REMOVE_ME, handleRemoveMe);
      }
    };
  }, [isConnected, handleRemoveMe]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (joinTimeoutRef.current) {
        globalThis.clearTimeout(joinTimeoutRef.current);
        joinTimeoutRef.current = null;
      }
      if (leaveTimeoutRef.current) {
        globalThis.clearTimeout(leaveTimeoutRef.current);
        leaveTimeoutRef.current = null;
      }
    };
  }, []);

  return null;
};

export default useParticipantNotifications;
